import type { CSSProperties } from "react";

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.trim().replace(/^#/, "");
  if (![3, 6].includes(cleaned.length)) return null;

  const full =
    cleaned.length === 3
      ? cleaned
          .split("")
          .map((c) => c + c)
          .join("")
      : cleaned;

  const num = Number.parseInt(full, 16);
  if (Number.isNaN(num)) return null;

  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn:
        h = ((gn - bn) / delta) % 6;
        break;
      case gn:
        h = (bn - rn) / delta + 2;
        break;
      default:
        h = (rn - gn) / delta + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s: s * 100, l: l * 100 };
}

function relativeLuminance(r: number, g: number, b: number): number {
  const srgb = [r, g, b].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

export function themeVarsFromHex(primaryHex: string): CSSProperties {
  const rgb = hexToRgb(primaryHex) ?? hexToRgb("#2563EB")!;
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Pick readable foreground for primary (white on dark colors, black on very light colors)
  const lum = relativeLuminance(rgb.r, rgb.g, rgb.b);
  const fg = lum > 0.6 ? "0 0% 0%" : "0 0% 100%";

  const h = Math.round(hsl.h);
  const s = Math.round(clamp(hsl.s, 0, 100));
  const l = Math.round(clamp(hsl.l, 0, 100));

  // Shadcn uses: hsl(var(--primary))
  return {
    // @ts-expect-error: CSS custom properties
    "--primary": `${h} ${s}% ${l}%`,
    // @ts-expect-error: CSS custom properties
    "--ring": `${h} ${s}% ${l}%`,
    // @ts-expect-error: CSS custom properties
    "--primary-foreground": fg,
  } as CSSProperties;
}


